import React from 'react';
import { Box, useTheme, useMediaQuery, alpha } from '@mui/material';
import { keyframes } from '@mui/system';

interface WaveLoadingAnimationProps {
  size?: 'small' | 'medium' | 'large';
}

// Define the wave animation
const waveAnimation = keyframes`
  0% { 
    transform: scale(0.8); 
    opacity: 0.6;
  }
  50% { 
    transform: scale(1.2); 
    opacity: 0.2;
  }
  100% { 
    transform: scale(0.8); 
    opacity: 0.6;
  }
`;

// Responsive sizes configuration
const sizes = {
  small: { 
    container: 60, 
    waves: [20, 35, 50],
    borderWidth: 2
  },
  medium: { 
    container: 80, 
    waves: [30, 50, 70],
    borderWidth: 2
  },
  large: { 
    container: 120, 
    waves: [40, 65, 90],
    borderWidth: 3
  }
};

const WaveLoadingAnimation: React.FC<WaveLoadingAnimationProps> = ({ size = 'medium' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use smaller size on mobile
  const actualSize = isMobile ? 'small' : size;
  const { container, waves, borderWidth } = sizes[actualSize];
  
  return (
    <Box
      sx={{
        position: 'relative',
        width: container,
        height: container,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      role="status"
      aria-label="Loading"
    >
      {waves.map((waveSize, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            width: waveSize,
            height: waveSize,
            border: `${borderWidth}px solid`,
            borderColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.light, 0.8 - index * 0.12)
              : alpha(theme.palette.primary.main, 0.5 - index * 0.1),
            borderRadius: '50%',
            animation: `${waveAnimation} 2.5s ease-in-out infinite`,
            animationDelay: `${index * 0.3}s`,
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </Box>
  );
};

export default WaveLoadingAnimation;